import axios from 'axios'
import hash from 'object-hash'
import { omit } from 'ramda'

import { Palette, commonChartOptions, range6m1yAll } from '../'
import {
  CompassAPIDataBase,
  CompassAPIMultiSeriesChartData,
  convertCompassToHighchartsDataFormat,
  getAreaRangeData,
  getCompassAPIChartData,
  getDataSources,
  getMultipleLineChartProps,
  getMultipleLineStrCategoriesChartProps,
} from '../utils/compass'
import { capitalizeString } from '../utils/string'
import type { BaseContext } from './'
import { CommonChartConfig } from './common'

const SUGAR_CHARTS_PATH = '/api/charts/sugar'

const SUGAR_ETHANOL_PATH =
  SUGAR_CHARTS_PATH + '/etanol/ratio-acucar-hidratado-monthly-ma'

const SUGAR_OBSERVATORIO_DA_CANA_CHARTS_PATH =
  SUGAR_CHARTS_PATH + '/observatorio-da-cana'

export namespace Sugar {
  export namespace Etanol {
    const CONTEXT_ID = 'equity:sugar:etanol'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'sb1_stock_total_use'
        | 'sb1_brl'
        | 'ratio_acucar_hidratado_weekly'
        | 'sb1_usd_speculative_position'
        | 'br_ratio_stock_demand_anidro'
        | 'br_ratio_stock_demand_hidratado'
        | 'br_ratio_stock_demand_total'
        | 'us_ratio_stock_demand'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'sb1_stock_total_use':
            return getSb1StockTotalUseChartProps(config, data)
          case 'ratio_acucar_hidratado_weekly':
            return getMultipleLineChartPropsWrapper(config, data)
          case 'sb1_usd_speculative_position':
            return getSb1UsdSpeculativePositionChartProps(config, data)
          case 'sb1_brl':
            return getMultipleLineChartPropsWrapper(
              config,
              {
                ...data,
                data: data.data.filter((value) => value.name !== 'mean'),
              },
              { avg: true, sd1: false, sd2: false }
            )
          case 'br_ratio_stock_demand_anidro':
          case 'br_ratio_stock_demand_hidratado':
          case 'br_ratio_stock_demand_total':
          case 'us_ratio_stock_demand':
            return getRatioChartProps(config, data)
        }
      },
    }

    const getRatioChartProps = (config: ChartConfig, data: ChartData) => {
      const rangeValues: Record<number, { min?: number; max?: number }> = {}
      convertCompassToHighchartsDataFormat(
        data.data.find(({ name }) => name.includes('_max'))?.data ?? []
      ).forEach(([x, value]) => {
        rangeValues[x] = { min: value }
      })
      convertCompassToHighchartsDataFormat(
        data.data.find(({ name }) => name.includes('_min'))?.data ?? []
      ).forEach(([x, value]) => {
        rangeValues[x] = { ...(rangeValues[x] ?? {}), max: value }
      })

      const series = [
        ...data.data
          .filter(
            ({ name }) =>
              !name.includes('_min') &&
              !name.includes('_max') &&
              !name.includes('_mean')
          )
          .map((s, idx) => ({
            id: `${config.chart}-${idx}`,
            type: 'spline',
            name: s.name,
            tooltip: { valueDecimals: 2 },
            yAxis: 'default',
            zIndex: 1,
            marker: { enabled: false },
            data: convertCompassToHighchartsDataFormat(s.data),
          })),
        {
          id: `${config.chart}-avg`,
          type: 'spline',
          color: Palette.darkYellow,
          dashStyle: 'LongDash',
          name: capitalizeString(
            data.data
              .find(({ name }) => name.includes('mean'))
              ?.name?.replaceAll('_', ' ') ?? ''
          ),
          tooltip: { valueDecimals: 2 },
          yAxis: 'default',
          zIndex: 1,
          marker: { enabled: false },
          data: convertCompassToHighchartsDataFormat(
            data.data.find(({ name }) => name.includes('mean'))?.data ?? []
          ),
        },
        {
          id: `${config.chart}-range`,
          type: 'arearange',
          color: Palette.fadedYellow,
          enableMouseTracking: false,
          showInLegend: false,
          grouping: false,
          yAxis: 'default',
          xAxis: 0,
          zIndex: 0,
          marker: { enabled: false },
          data: getAreaRangeData(rangeValues),
        },
      ]

      const yAxis = [{ id: 'default', opposite: false }]
      const xAxis = [{ labels: { format: '{value:%b}' } }]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          rangeSelector: range6m1yAll,
          tooltip: { xDateFormat: '%A, %d de %B' },
          series,
          xAxis,
          yAxis,
        },
        stockTools: true,
      }
    }

    const getMultipleLineChartPropsWrapper = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData,
      msdIndicators: { avg: boolean; sd1: boolean; sd2: boolean } = {
        avg: false,
        sd1: false,
        sd2: false,
      }
    ) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        chartTitle: config.title,
        msdIndicators: msdIndicators,
      })(config, data)

    const getSb1StockTotalUseChartProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        convertToPercentages: [false, true],
        yAxis: [
          { id: 'sb1', title: { text: 'c/lb' } },
          {
            id: 'stock to total use',
            title: { text: 'Stock to use - Inverted' },
            opposite: true,
            reversed: true,
            labelSuffix: '%',
          },
        ],
      },
      { yAxis: ['sb1', 'stock to total use'] }
    )
    const getSb1UsdSpeculativePositionChartProps = getMultipleLineChartProps(
      Context,
      {
        convertToPercentages: [true, false],
        yAxis: [
          {
            id: 'speculative_positions_share',
            title: { text: '%' },
            opposite: true,
            labelSuffix: '%',
          },
          { id: 'sb1_comdty', title: { text: 'USD/lb' } },
        ],
      },
      {
        yAxis: ['speculative_positions_share', 'sb1_comdty'],
      }
    )
  }

  export namespace EtanolRatioAcucar {
    const CONTEXT_ID = 'equity:sugar:etanol_ratio_acucar'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title?: string
      pointsCount: number
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    interface RatioAcucarChartData extends CompassAPIDataBase {
      title: string
      data: {
        name: string
        values: {
          date: string
          value: number
        }[]
      }[]
    }

    const convertFormat = (data: RatioAcucarChartData) =>
      ({
        ...data,
        data: data.data.map((entry) => ({
          ...omit(['values'], entry),
          data: entry.values,
        })),
      } as ChartData)

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: SUGAR_ETHANOL_PATH,
        method: 'POST',
        data: {
          value_names: ['ratio_acucar_1st_future_hidratado_monthly'],
          mm: config.pointsCount,
        },
      }).then((response) => convertFormat(response.data))

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.pointsCount}`,
      getChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(
          Context,
          {},
          { type: Array(data.data.length).fill('spline') }
        )(config, data),
    }
  }

  export namespace ObservatorioDaCana {
    const CONTEXT_ID = 'equity:sugar:observatorio_da_cana'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    type Region = 'Centro-Sul' | 'Demais Estados' | 'São Paulo'

    type ObservatorioDaCanaChartId =
      | 'historico_producao_acucar'
      | 'historico_producao_etanol'
      | 'historico_mix_acucar'
      | 'historico_moagem_cana'

    export interface ChartConfig {
      title?: string
      chart: ObservatorioDaCanaChartId
      regions: Region[]
      seriesType: 'acumulada' | 'quinzenal'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    interface ObservatorioDaCanaChartData extends CompassAPIDataBase {
      title: string
      data: {
        name: string
        series_id: string
        values: {
          x_value: string
          y_value: number
        }[]
      }[]
    }

    const SUGAR_OBSERVATORIO_DA_CANA_INDICADORES: Record<
      ObservatorioDaCanaChartId,
      string
    > = {
      historico_producao_acucar: 'Açúcar',
      historico_producao_etanol: 'Etanol total',
      historico_mix_acucar: 'Mix açúcar',
      historico_moagem_cana: 'Cana-de-Açúcar',
    }

    const CHART_TITLES: Record<ObservatorioDaCanaChartId, string> = {
      historico_producao_acucar:
        'Histórico Da Produção de Açúcar (Mil Toneladas), Acumulada, Da Safra',
      historico_producao_etanol:
        'Histórico Da Produção de Etanol Total (Mil M³), Acumulada, Da Safra',
      historico_mix_acucar: 'Histórico de Mix Açúcar (%), Acumulada, Da Safra',
      historico_moagem_cana:
        'Histórico Da Moagem de Cana-de-açúcar (Mil Toneladas), Acumulada, Da Safra',
    }

    const convertFormat = (data: ObservatorioDaCanaChartData) =>
      ({
        ...data,
        data: data.data.map((entry) => ({
          ...omit(['values'], entry),
          data: entry.values.map((point) => ({
            date: point.x_value,
            value: point.y_value,
          })),
        })),
      } as ChartData)

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: SUGAR_OBSERVATORIO_DA_CANA_CHARTS_PATH,
        method: 'POST',
        data: {
          indicador: SUGAR_OBSERVATORIO_DA_CANA_INDICADORES[config.chart],
          region: config.regions,
          series_type: config.seriesType,
          value_name: 'safra_atual',
        },
      }).then((response) => convertFormat(response.data))

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData,
      getChartProps: (config, data) =>
        getMultipleLineStrCategoriesChartProps(
          Context,
          {
            chartTitle: CHART_TITLES[config.chart],
            forceXAxisCategories: false,
            convertToPercentages: Array(data.data.length).fill(
              config.chart === 'historico_mix_acucar'
            ),
          },
          { type: Array(data.data.length).fill('spline') }
        )(config, data),
    }
  }
}
